import { useEffect, useState } from 'react';
import type { CustomerApiClient } from '../api-client.ts';
import { Button, Card, UnavailableSection } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import { requestWithRetry } from '../request-retry.ts';

type AccountHub = Awaited<ReturnType<CustomerApiClient['account']['hub']>>;

const ACCOUNT_LINKS = [
  { href: '/orders', label: 'Order history', section: 'ORDERS' },
  { href: '/account/favorites', label: 'Favorites', section: 'FAVORITES' },
  { href: '/account/lists', label: 'Shopping lists', section: 'LISTS' },
  { href: '/beauty-burst-plus', label: 'Beauty Burst+', section: 'MEMBERSHIP' },
  { href: '/account/addresses', label: 'Addresses', section: 'ADDRESSES' },
  { href: '/account/notifications', label: 'Notifications', section: 'NOTIFICATIONS' },
] as const;

export interface AccountScreenProps {
  hub: AccountHub;
  client: Pick<CustomerApiClient, 'account' | 'analytics'>;
  onSignOut?: () => void | Promise<void>;
}

/** Customer account navigation is data-driven from the account service's availability contract. */
export function AccountScreen({ hub, client, onSignOut }: AccountScreenProps) {
  const displayName =
    hub.profile.display_name ??
    [hub.profile.first_name, hub.profile.last_name].filter((part) => part !== null).join(' ') ??
    'Your account';
  const unavailable = new Map(hub.sections.map((entry) => [entry.section, entry]));
  const [consents, setConsents] = useState({ ANALYTICS: false, MARKETING_EMAIL: false });
  const [consentError, setConsentError] = useState<string | null>(null);
  const [savingConsent, setSavingConsent] = useState<keyof typeof consents | null>(null);

  useEffect(() => {
    let active = true;
    void requestWithRetry(() => client.account.consents())
      .then((history) => {
        if (!active) return;
        const current = {
          ANALYTICS: history.find((entry) => entry.consent_type === 'ANALYTICS')?.granted ?? false,
          MARKETING_EMAIL:
            history.find((entry) => entry.consent_type === 'MARKETING_EMAIL')?.granted ?? false,
        };
        setConsents(current);
        client.analytics?.setAnalyticsConsent?.(current.ANALYTICS);
      })
      .catch(() => {
        if (active) setConsentError('Your privacy choices could not be loaded. Please try again.');
      });
    return () => {
      active = false;
    };
  }, [client]);

  const changeConsent = (consentType: keyof typeof consents, granted: boolean) => {
    setSavingConsent(consentType);
    setConsentError(null);
    void client.account
      .recordConsent({ consentType, granted })
      .then(() => {
        setConsents((current) => ({ ...current, [consentType]: granted }));
        if (consentType === 'ANALYTICS') client.analytics?.setAnalyticsConsent?.(granted);
      })
      .catch((error: unknown) =>
        setConsentError(
          error instanceof Error ? error.message : 'Your privacy choice could not be saved.',
        ),
      )
      .finally(() => setSavingConsent(null));
  };

  return (
    <section aria-labelledby="account-title">
      <header style={{ marginBottom: space[6] }}>
        <h1
          id="account-title"
          style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}
        >
          Account
        </h1>
        <p style={{ ...typeStyle('body'), color: semanticColor.textSecondary, marginBottom: 0 }}>
          {displayName}
        </p>
      </header>

      <section aria-label="Account areas" style={{ display: 'grid', gap: space[3] }}>
        {ACCOUNT_LINKS.map((item) => {
          const state = unavailable.get(item.section);
          return (
            <Card key={item.href}>
              <a
                href={customerHref(item.href)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: '44px',
                  color: semanticColor.brandContrast,
                  fontWeight: 700,
                }}
              >
                {item.label}
                {state?.available === false ? (
                  <span>Coming later</span>
                ) : (
                  <span aria-hidden="true">→</span>
                )}
              </a>
            </Card>
          );
        })}
      </section>

      <section aria-labelledby="payment-methods-title" style={{ marginTop: space[6] }}>
        <h2
          id="payment-methods-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Payment methods
        </h2>
        <UnavailableSection
          title="Payment methods"
          reason="Payment methods are not available yet. Card entry and payment storage are not offered here."
        />
      </section>

      <section aria-labelledby="privacy-choices-title" style={{ marginTop: space[6] }}>
        <h2
          id="privacy-choices-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Privacy choices
        </h2>
        <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
          Choose whether Beauty Burst may use analytics or send marketing email. Both choices are
          off unless you turn them on.
        </p>
        <div style={{ display: 'grid', gap: space[3] }}>
          <label
            style={{ display: 'flex', gap: space[2], alignItems: 'center', minHeight: '44px' }}
          >
            <input
              data-testid="checkbox-analytics-consent"
              type="checkbox"
              checked={consents.ANALYTICS}
              disabled={savingConsent === 'ANALYTICS'}
              onChange={(event) => changeConsent('ANALYTICS', event.currentTarget.checked)}
            />
            Allow first-party analytics
          </label>
          <label
            style={{ display: 'flex', gap: space[2], alignItems: 'center', minHeight: '44px' }}
          >
            <input
              data-testid="checkbox-marketing-email-consent"
              type="checkbox"
              checked={consents.MARKETING_EMAIL}
              disabled={savingConsent === 'MARKETING_EMAIL'}
              onChange={(event) => changeConsent('MARKETING_EMAIL', event.currentTarget.checked)}
            />
            Allow marketing email
          </label>
        </div>
        {consentError === null ? null : <p role="alert">{consentError}</p>}
      </section>

      <section aria-labelledby="later-account-title" style={{ marginTop: space[6] }}>
        <h2
          id="later-account-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          More account areas
        </h2>
        <div style={{ display: 'grid', gap: space[3] }}>
          <UnavailableSection
            title="Reviews"
            reason={
              unavailable.get('REVIEWS')?.reason ?? 'Reviews will be available in a later phase.'
            }
          />
          <Card>
            <a
              href={customerHref('/account/privacy')}
              style={{ display: 'inline-flex', minHeight: '44px', alignItems: 'center' }}
            >
              Privacy controls
            </a>
          </Card>
        </div>
      </section>

      <Button
        variant="secondary"
        onClick={() => {
          void onSignOut?.();
        }}
        style={{ marginTop: space[6] }}
      >
        Sign out
      </Button>
    </section>
  );
}
